import React, { useEffect, useState, useRef } from "react";
import { FaTimes, FaCloudUploadAlt, FaTrash } from "react-icons/fa";

const GENDERS = ["Woman", "Man", "Transgender Woman", "Transgender Man", "Non-binary", "Genderqueer"];
const AGE_GROUPS = [
  "Child",
  "Young Adult",
  "Adult",
  "Middle-aged",
  "Senior"
];
const SKIN_TONES = ["Fair", "Wheatish", "Dusky", "Dark"];
const HAIR_STYLES = ["Straight", "Wavy", "Curly", "Long", "Short", "Bald"];
const FACIAL_HAIR = ["Beard", "Moustache", "Clean Shaven"];
const BODY_TYPES = ["Slim", "Athletic", "Average", "Heavyset", "Plus Size"];
const ETHNICITIES = ["India", "East Asian", "Southeast Asian", "Middle Eastern", "African", "European", "Hispanic", "Mixed"];

// Default selection values for the human model modal
const DEFAULT_MODEL = {
  gender: "Woman",
  ageGroup: "Adult",
  skinTone: "Fair",
  hairStyle: "Long",
  bodyType: "Slim",
  ethnicity: "India",
};

const HumanModelModal = ({
  isOpen,
  onClose,
  selectedHumanModel,
  setSelectedHumanModel,
  isPreview = false,
  className = ""
}) => {
  const [temp, setTemp] = useState(DEFAULT_MODEL);
  const [uploadedImage, setUploadedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (isOpen && !isPreview) {
      const model = selectedHumanModel && Object.keys(selectedHumanModel).length > 0 ? selectedHumanModel : DEFAULT_MODEL;
      
      if (model.isImageUpload && model.uploadedImageFile) {
        setUploadedImage(model.uploadedImageFile);
        setImagePreview(prevPreview => {
          if (prevPreview && prevPreview.startsWith('blob:')) {
            URL.revokeObjectURL(prevPreview);
          }
          return URL.createObjectURL(model.uploadedImageFile);
        });
        setTemp(DEFAULT_MODEL);
      } else {
        setUploadedImage(null);
        setImagePreview(prevPreview => {
          if (prevPreview && prevPreview.startsWith('blob:')) {
            URL.revokeObjectURL(prevPreview);
          }
          return null;
        });
        setTemp(model);
      }
    }
  }, [isOpen, isPreview, selectedHumanModel]);

  // Cleanup preview URL on unmount
  useEffect(() => {
    return () => {
      if (imagePreview && imagePreview.startsWith('blob:')) {
        URL.revokeObjectURL(imagePreview);
      }
    };
  }, [imagePreview]);

  if (!isOpen && !isPreview) return null;

  const canShowFacialHair = (temp.gender === "Male" || temp.gender === "Man" || temp.gender === "Boy");

  const toggleChip = (key, value, multi = false) => {
    if (isPreview) return;
    // If uploading an image, clear characteristics
    if (uploadedImage) {
      setUploadedImage(null);
      if (imagePreview && imagePreview.startsWith('blob:')) {
        URL.revokeObjectURL(imagePreview);
      }
      setImagePreview(null);
    }
    if (multi) {
      const current = Array.isArray(temp[key]) ? temp[key] : [];
      const exists = current.includes(value);
      const updated = exists ? current.filter(v => v !== value) : [...current, value];
      setTemp({ ...temp, [key]: updated });
    } else {
      setTemp({ ...temp, [key]: temp[key] === value ? undefined : value });
    }
  };

  // Handle image upload with validation and in-memory cloning to avoid ERR_UPLOAD_FILE_CHANGED
  const handleImageUpload = async (e) => {
    if (isPreview) return;
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type (JPG/PNG only)
    const validImageTypes = ['image/jpeg', 'image/jpg', 'image/png'];
    if (!validImageTypes.includes(file.type)) {
      alert('Please upload only JPG or PNG image files.');
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    // Validate file size (max 10MB)
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      alert('Image size should be less than 10MB.');
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    // Clone file into memory so future disk changes don't invalidate the upload handle
    let inMemoryFile = file;
    try {
      const buffer = await file.arrayBuffer();
      inMemoryFile = new File([buffer], file.name, { type: file.type, lastModified: file.lastModified || Date.now() });
    } catch (cloneErr) {
      console.warn('Could not clone human model file, using original reference', cloneErr);
    }

    // Create preview
    const preview = URL.createObjectURL(inMemoryFile);
    
    // Clear previous preview if exists
    if (imagePreview && imagePreview.startsWith('blob:')) {
      URL.revokeObjectURL(imagePreview);
    }

    setUploadedImage(inMemoryFile);
    setImagePreview(preview);
    
    // Clear characteristics when image is uploaded
    setTemp(DEFAULT_MODEL);
    
    // Reset file input
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // Remove uploaded image
  const handleRemoveImage = () => {
    if (isPreview) return;
    if (imagePreview && imagePreview.startsWith('blob:')) {
      URL.revokeObjectURL(imagePreview);
    }
    setUploadedImage(null);
    setImagePreview(null);
    setTemp(DEFAULT_MODEL);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleApply = () => {
    // If image is uploaded, send image; otherwise send characteristics
    if (uploadedImage) {
      setSelectedHumanModel({
        uploadedImageFile: uploadedImage,
        imagePreview: imagePreview,
        isImageUpload: true
      });
    } else {
      setSelectedHumanModel(temp || {});
    }
    if (typeof onClose === 'function') onClose();
  };

  const handleCancel = () => {
    if (typeof onClose === 'function') onClose();
  };

  const handleReset = () => {
    if (isPreview) return;
    setTemp(DEFAULT_MODEL);
    if (uploadedImage) {
      handleRemoveImage();
    }
  };

  const Panel = (
    <div className={`glass-effect rounded-2xl border border-white/10 bg-[#111111]/95 backdrop-blur-2xl p-5 w-[34rem] sm:w-[40rem] md:w-[44rem] max-h-[70vh] overflow-y-auto ${isPreview ? '' : 'shadow-2xl'}`}>
      <div className="flex items-center gap-2 mb-2">
        <h3 className="text-sm font-medium text-white tracking-wide">Define the human model</h3>
        {!isPreview && (
          <button type="button" className="ml-auto text-gray-300 hover:text-white" onClick={onClose} title="Close">
            <FaTimes className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      {/* Image Upload Section */}
      <div className="space-y-3">
        <div className="text-[11px] text-gray-300 mb-2">Upload Human Model Image</div>
        {!uploadedImage ? (
          <label
            htmlFor="human-model-image-upload"
            className={`flex flex-col items-center justify-center w-full h-32 sm:h-40 md:h-48 border-2 border-dashed rounded-lg cursor-pointer transition-colors ${
              isPreview 
                ? 'border-white/10 bg-[#0f0f0f]/50' 
                : 'border-white/20 bg-[#0f0f0f]/30 hover:border-white/30 hover:bg-[#0f0f0f]/40'
            }`}
            onDragOver={(ev) => { ev.preventDefault(); ev.stopPropagation(); }}
            onDrop={(ev) => {
              ev.preventDefault();
              ev.stopPropagation();
              const files = ev.dataTransfer?.files;
              if (files && files[0]) {
                // build a synthetic event matching input change signature
                handleImageUpload({ target: { files } });
              }
            }}
          >
            <input
              id="human-model-image-upload"
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/jpg,image/png"
              onChange={handleImageUpload}
              disabled={isPreview}
              className="hidden"
            />
            <FaCloudUploadAlt className="w-6 h-6 sm:w-8 sm:h-8 text-gray-400 mb-2" />
            <p className="text-[11px] sm:text-[12px] text-gray-400 text-center px-4">
              <span className="text-white font-medium">Click to upload</span> or drag and drop
            </p>
            <p className="text-[10px] sm:text-[11px] text-gray-500 mt-1">JPG or PNG (max 10MB)</p>
          </label>
        ) : (
          <div className="relative w-full">
            <div className="relative rounded-lg overflow-hidden border border-white/10 bg-[#0f0f0f]">
              <img
                src={imagePreview}
                alt="Uploaded human model"
                className="w-full h-auto max-h-64 sm:max-h-80 md:max-h-96 object-contain"
              />
              {!isPreview && (
                <button
                  type="button"
                  onClick={handleRemoveImage}
                  className="absolute top-2 right-2 p-1.5 sm:p-2 bg-red-500/90 hover:bg-red-600 rounded-full transition-colors"
                  title="Remove image"
                >
                  <FaTrash className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-white" />
                </button>
              )}
            </div>
            <p className="text-[10px] sm:text-[11px] text-gray-500 mt-2 text-center">
              {uploadedImage.name}
            </p>
          </div>
        )}
      </div>

      {!isPreview && (
        <div className="mt-4 flex items-center">
          <button type="button" onClick={handleReset} className="text-[12px] text-gray-300 hover:text-white">Reset</button>
          <div className="ml-auto flex items-center gap-2">
            <button type="button" onClick={handleCancel} className="inline-flex items-center rounded-lg bg-white/10 text-gray-100 px-3 py-1.5 text-[12px] border border-white/10 hover:bg-white/20">Cancel</button>
            <button type="button" onClick={handleApply} className="inline-flex items-center rounded-lg bg-white text-black px-3 py-1.5 text-[12px] hover:opacity-90">Apply ▶</button>
          </div>
        </div>
      )}

      {/* OR Separator */}
      <div className="relative my-6 md:my-8">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t border-white/20"></div>
        </div>
        <div className="relative flex justify-center">
          <span className="bg-[#111111] px-4 text-[11px] text-gray-400 font-medium">OR</span>
        </div>
      </div>
      
      <div className="text-[11px] text-gray-400 mb-3">Choose details to generate your model.</div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-3">
          <div>
            <div className="text-[11px] text-gray-300 mb-1">Gender</div>
            <div className="flex flex-wrap gap-1.5">
              {GENDERS.map(g => (
                <button
                  key={g}
                  type="button"
                  disabled={isPreview}
                  className={`px-2.5 py-1 rounded-full text-[11px] border ${temp.gender === g ? 'bg-white text-black border-white' : 'bg-[#0f0f0f] text-gray-200 border-white/10 hover:bg-white/10'}`}
                  onClick={() => toggleChip('gender', g, false)}
                  title={g}
                >
                  {g}
                </button>
              ))}
            </div>
          </div>

          <div>
            <div className="text-[11px] text-gray-300 mb-1">Age Group</div>
            <div className="flex flex-wrap gap-1.5">
              {AGE_GROUPS.map(a => (
                <button
                  key={a}
                  type="button"
                  disabled={isPreview}
                  className={`px-2.5 py-1 rounded-full text-[11px] border ${temp.ageGroup === a ? 'bg-white text-black border-white' : 'bg-[#0f0f0f] text-gray-200 border-white/10 hover:bg-white/10'}`}
                  onClick={() => toggleChip('ageGroup', a, false)}
                  title={a}
                >
                  {a}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="space-y-3">
          <div>
            <div className="text-[11px] text-gray-300 mb-1">Skin Tone</div>
            <div className="flex flex-wrap gap-1.5">
              {SKIN_TONES.map(t => (
                <button
                  key={t}
                  type="button"
                  disabled={isPreview}
                  className={`px-2.5 py-1 rounded-full text-[11px] border ${temp.skinTone === t ? 'bg-white text-black border-white' : 'bg-[#0f0f0f] text-gray-200 border-white/10 hover:bg-white/10'}`}
                  onClick={() => toggleChip('skinTone', t, false)}
                  title={t}
                >
                  {t}
                </button>
              ))}
            </div>
          </div>

          <div>
            <div className="text-[11px] text-gray-300 mb-1">Hair Style</div>
            <div className="flex flex-wrap gap-1.5">
              {HAIR_STYLES.map(h => (
                <button
                  key={h}
                  type="button"
                  disabled={isPreview}
                  className={`px-2.5 py-1 rounded-full text-[11px] border ${temp.hairStyle === h ? 'bg-white text-black border-white' : 'bg-[#0f0f0f] text-gray-200 border-white/10 hover:bg-white/10'}`}
                  onClick={() => toggleChip('hairStyle', h, false)}
                  title={h}
                >
                  {h}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="space-y-3 md:col-span-2">
          <div className="text-[11px] text-gray-300 mb-1">Ethnicity</div>
          <div className="flex flex-wrap gap-1.5">
            {ETHNICITIES.map(e => (
              <button
                key={e}
                type="button"
                disabled={isPreview}
                className={`px-2.5 py-1 rounded-full text-[11px] border ${temp.ethnicity === e ? 'bg-white text-black border-white' : 'bg-[#0f0f0f] text-gray-200 border-white/10 hover:bg-white/10'}`}
                onClick={() => toggleChip('ethnicity', e, false)}
                title={e}
              >
                {e}
              </button>
            ))}
          </div>
        </div>

        {canShowFacialHair && (
          <div className="space-y-3 md:col-span-2">
            <div className="text-[11px] text-gray-300 mb-1">Facial Hair</div>
            <div className="flex flex-wrap gap-1.5">
              {FACIAL_HAIR.map(f => (
                <button
                  key={f}
                  type="button"
                  disabled={isPreview}
                  className={`px-2.5 py-1 rounded-full text-[11px] border ${temp.facialHair === f ? 'bg-white text-black border-white' : 'bg-[#0f0f0f] text-gray-200 border-white/10 hover:bg-white/10'}`}
                  onClick={() => toggleChip('facialHair', f, false)}
                  title={f}
                >
                  {f}
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="space-y-3 md:col-span-2">
          <div className="text-[11px] text-gray-300 mb-1">Body Type</div>
          <div className="flex flex-wrap gap-1.5">
            {BODY_TYPES.map(b => (
              <button
                key={b}
                type="button"
                disabled={isPreview}
                className={`px-2.5 py-1 rounded-full text-[11px] border ${temp.bodyType === b ? 'bg-white text-black border-white' : 'bg-[#0f0f0f] text-gray-200 border-white/10 hover:bg-white/10'}`}
                onClick={() => toggleChip('bodyType', b, false)}
                title={b}
              >
                {b}
              </button>
            ))}
          </div>
        </div>
      </div>


      {!isPreview && (
        <div className="mt-4 flex items-center">
          <button type="button" onClick={handleReset} className="text-[12px] text-gray-300 hover:text-white">Reset</button>
          <div className="ml-auto flex items-center gap-2">
            <button type="button" onClick={handleCancel} className="inline-flex items-center rounded-lg bg-white/10 text-gray-100 px-3 py-1.5 text-[12px] border border-white/10 hover:bg-white/20">Cancel</button>
            <button type="button" onClick={handleApply} className="inline-flex items-center rounded-lg bg-white text-black px-3 py-1.5 text-[12px] hover:opacity-90">Apply ▶</button>
          </div>
        </div>
      )}
    </div>
  );

  if (isPreview) {
    return (
      <div className={`absolute z-50 pointer-events-auto ${className}`}>
        {Panel}
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      {Panel}
    </div>
  );
};

export default HumanModelModal; 